/**
 * Thin wrapper around @microsoft/fetch-event-source for streaming device logs.
 *
 * Handles:
 *  - Token injection from localStorage (same source as axiosInstance)
 *  - Reconnection callbacks
 *  - Type-safe event parsing for `log`, `ready`, `heartbeat`
 */

import {
  fetchEventSource,
  EventSourceMessage,
} from "@microsoft/fetch-event-source";

export interface DeviceLogEvent {
  event_time: string;
  notification_type: string;
  device: string;
  connector_id: number;
  meta_data: Record<string, unknown>;
  session_event_name?: string;
}

export type StreamStatus = "connecting" | "open" | "closed" | "error";

export interface OpenDeviceLogStreamOptions {
  onLog: (event: DeviceLogEvent) => void;
  onReady?: (info: { device_id: number; unique_name: string }) => void;
  onStatus?: (status: StreamStatus) => void;
  signal: AbortSignal;
}

// Strip trailing slash so string concat doesn't produce `//api/...`.
// Axios handles this automatically for the rest of the app; fetch does not.
const API_BASE = (
  process.env.REACT_APP_API_URL || "https://admin-api.cms.chargingadda.com"
).replace(/\/+$/, "");

class RetriableError extends Error {}
class FatalError extends Error {}

export async function openDeviceLogStream(
  deviceId: number,
  opts: OpenDeviceLogStreamOptions,
  tenantId?: string
): Promise<void> {
  opts.onStatus?.("connecting");

  // Maintenance uses /api/maintenance/devices/{id}/logs/stream
  // Tenant-scoped uses /api/stations/tenants/{tenantId}/devices/{id}/logs/stream
  const url = tenantId
    ? `${API_BASE}/api/stations/tenants/${tenantId}/devices/${deviceId}/logs/stream`
    : `${API_BASE}/api/maintenance/devices/${deviceId}/logs/stream`;

  await fetchEventSource(
    url,
    {
      method: "GET",
      // Read token fresh on every (re)connect — picks up refreshed tokens.
      headers: buildHeaders(),
      signal: opts.signal,
      openWhenHidden: true, // keep streaming when tab is backgrounded
      async onopen(res) {
        if (res.ok && res.headers.get("content-type")?.startsWith("text/event-stream")) {
          opts.onStatus?.("open");
          return;
        }
        if (res.status === 401 || res.status === 403) {
          // No point retrying on auth failures
          throw new FatalError(`Auth failed (${res.status})`);
        }
        throw new RetriableError(`SSE open failed: ${res.status}`);
      },
      onmessage(ev: EventSourceMessage) {
        if (ev.event === "log") {
          try {
            const parsed: DeviceLogEvent = JSON.parse(ev.data);
            opts.onLog(parsed);
          } catch {
            // ignore bad JSON
          }
        } else if (ev.event === "ready") {
          try {
            opts.onReady?.(JSON.parse(ev.data));
          } catch {
            /* noop */
          }
        }
        // "heartbeat" events are ignored intentionally
      },
      onerror(err) {
        if (err instanceof FatalError) {
          opts.onStatus?.("error");
          throw err; // stop retrying
        }
        opts.onStatus?.("error");
        // Return undefined → fetch-event-source uses default backoff and retries
      },
      onclose() {
        opts.onStatus?.("closed");
        // Returning triggers onclose handling — throwing would retry. We let
        // fetch-event-source reconnect by default.
        throw new RetriableError("stream closed");
      },
    }
  );
}

function buildHeaders(): Record<string, string> {
  const token = localStorage.getItem("access_token");
  const headers: Record<string, string> = {
    Accept: "text/event-stream",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}
